/**
 * Compliance Dashboard API Service
 *
 * Type-safe compliance dashboard endpoints using openapi-fetch.
 * Types derived from platform-admin.openapi.yaml -- no manual interfaces.
 *
 * GH #2917, comment item 9. The four list readers returned `data.trend ?? []`,
 * `data.buckets ?? []`, `data.reports ?? []` and `data.alerts ?? []`, and
 * rewrapped each one in a fresh object. Every one of those lists is OPTIONAL in
 * the canonical spec (schema-registry/platform-admin.openapi.yaml, the
 * regulation-u-trend, risk-distribution, edd-reports and alerts responses
 * declare no required fields), so a 200 that omits its list is contract-valid
 * and says nothing about the institution's compliance posture. The coercion
 * turned that silence into a measured empty list, and ComplianceStatus.tsx then
 * printed a measured-empty sentence over a list it never received. That is the
 * success-branch coercion `.claude/rules/no-fabricated-values.md` forbids, and
 * it is the shape collateral-monitoring.ts removed for deliverable 2.
 *
 * The body now reaches the caller exactly as it was read. An absent list
 * arrives as `undefined` and the page decides what that means; an empty array
 * arrives as an empty array and stays a measurement.
 */

import { platformAdminClient } from '../client';
import type { components } from '@aaimplatform/institutional-types/platform-admin';

export type ComplianceDashboardSummary = components['schemas']['ComplianceDashboardSummary'];
export type RegulationUDayData = components['schemas']['RegulationUDayData'];
export type RiskScoreBucket = components['schemas']['RiskScoreBucket'];
export type EDDReport = components['schemas']['EDDReport'];
export type ComplianceDashboardAlert = components['schemas']['ComplianceDashboardAlert'];

export const complianceDashboardApi = {
  getSummary: async (): Promise<ComplianceDashboardSummary> => {
    const { data, error } = await platformAdminClient.GET('/compliance/dashboard/summary');
    if (error) throw new Error('Failed to fetch compliance dashboard summary');
    return data;
  },

  getRegulationUTrend: async (days?: number) => {
    const { data, error } = await platformAdminClient.GET('/compliance/dashboard/regulation-u-trend', {
      params: { query: { days } },
    });
    if (error) throw new Error('Failed to fetch regulation U trend');
    return data;
  },

  getRiskDistribution: async () => {
    const { data, error } = await platformAdminClient.GET('/compliance/dashboard/risk-distribution');
    if (error) throw new Error('Failed to fetch risk distribution');
    return data;
  },

  getEddReports: async () => {
    const { data, error } = await platformAdminClient.GET('/compliance/dashboard/edd-reports');
    if (error) throw new Error('Failed to fetch EDD reports');
    return data;
  },

  getAlerts: async (hours?: number) => {
    const { data, error } = await platformAdminClient.GET('/compliance/dashboard/alerts', {
      params: { query: { hours } },
    });
    if (error) throw new Error('Failed to fetch compliance alerts');
    return data;
  },
};
